import {
  ContextualMenu,
  DefaultButton,
  IDragOptions,
  Modal,
  Stack,
  Text,
  TooltipHost,
  useTheme,
} from '@fluentui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { CloseButton } from 'common/components/Buttons';
import {
  FormHookAmount,
  FormHookDropdown,
  FormHookTextField,
} from 'common/components/FormHooksFields';
import { FormHookCheckBox } from 'common/components/FormHooksFields/FormHookCheckBox';
import { OnRenderRankLabel } from 'common/components/RankOptionUpdateComponent/OnRenderRankLabel';
import { OptionWithIcons } from 'common/components/RankOptionUpdateComponent/OptionWithIcons';
import { RankOptionDelete } from 'common/components/RankOptionUpdateComponent/RankOptionDelete';
import { RankOptionViewModal } from 'common/components/RankOptionUpdateComponent/RankOptionViewModal';
import { rankOptionType } from 'common/components/RankOptionUpdateComponent/types';
import { useCommonStyles } from 'common/styles';
import { EntityDeleteInput } from 'common/types/globalTypes';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { ApprovalSetupFlowCommonData } from 'settings/account/intuitiveApprovalSetupFlow/__generated__/ApprovalSetupFlowCommonData';
import { REQUESTOR_INITIAL_VALUES } from 'settings/account/intuitiveApprovalSetupFlow/ApprovalFlowDesigner/constants';
import { RequestorConditionsValues } from 'settings/account/intuitiveApprovalSetupFlow/ApprovalFlowDesigner/types';
import { useDropdownOptions } from './util';
import { validationFormSchema } from './valdation';

const dragOptions: IDragOptions = {
  moveMenuItemText: 'Move',
  closeMenuItemText: 'Close',
  menu: ContextualMenu,
  dragHandleSelector: '.ms-Modal-scrollableContent > div:first-child',
};
export const FormView: React.FC<{
  data?: any;
  onClose: () => void;
  onSave: (formFields: any) => void;
  approvalTypes: ApprovalSetupFlowCommonData | undefined;
  visibility: boolean;
  handleUserRankUpdate?: (newId: string) => void;
}> = ({
  onClose,
  approvalTypes,
  onSave,
  data,
  visibility,
  handleUserRankUpdate,
}) => {
    const theme = useTheme();

    const isNew = data === undefined;
    const [openRankModal, setOpenRankModal] = useState<boolean>(false);
    const [rankDeleteDialog, setRankDeleteDialog] = useState(false);
    const [rankDeleteEntity, setRankDeleteEntity] = useState<
      EntityDeleteInput | undefined
    >(undefined);
    const [selectedOption, setSelectedOption] = useState<
      rankOptionType | undefined
    >(undefined);
    const {
      currencies,
      conversionOptions,
      occupations,
      purchaseOrder,
      tags,
      ranks,
      rowSecurity,
      companies,
      additionalBusUnit,
      users,
      departmentTypes,
      departments,
      departmentGroups,
    } = useDropdownOptions(approvalTypes);

    const formMethods = useForm<RequestorConditionsValues>({
      mode: 'all',
      resolver: yupResolver(validationFormSchema),
    });

    const currentuserRankId = formMethods.watch('requesterUserRankId');
    const commonStyles = useCommonStyles();
    const {
      getValues,
      trigger,
      reset,
      setValue,
      formState: { isDirty, isValid },
    } = { ...formMethods };
    const onHandleSave = () => {
      const values = getValues();
      onSave(values);
    };
    useEffect(() => {
      if (visibility) {
        reset(
          data
            ? ({ ...data } as RequestorConditionsValues)
            : REQUESTOR_INITIAL_VALUES
        );
      }
      trigger();
    }, [data?.id, trigger, reset, visibility]);

    const AMOUNT_RANGE_TOOLTIP = (
      <>
        Enter the amount range that qualifies this approval flow for use.
        <br />
        (Leave blank if no restrictions apply)
      </>
    );

    const CURRENCY_TOOLTIP = (
      <>
        Enter a currency if this approval flow should only be used for a specific
        currency.
        <br />
        (Leave blank if no restrictions apply)
      </>
    );

    const CONVERSION_TOOLTIP = (
      <>
        Enter a conversion option if the amount should be converted before
        evaluating the amount range.
        <br />
        (Leave blank if no restrictions apply)
      </>
    );

    const OCCUPATION_TOOLTIP = (
      <>
        Select an occupation if this approval flow should only apply when the
        requester holds a specific occupation.
        <br />
        (Leave blank if no restrictions apply)
      </>
    );

    const DEPARTMENT_TOOLTIP = (
      <>
        If the primary department of the requested transaction matches this
        department, this approval flow will be eligible for use.
        <br />
        (Leave blank if no restrictions apply)
      </>
    );

    const DEPARTMENT_TYPE_TOOLTIP = (
      <>
        Select a department type if the primary department of the requested
        transaction belongs to a specific department type.
        <br />
        (Leave blank if no restrictions apply)
      </>
    );

    const DEPARTMENT_GROUP_TOOLTIP = (
      <>
        If the primary department is included in the selected department group,
        this approval flow will be eligible for use.
        <br />
        (Leave blank if no restrictions apply)
      </>
    );

    const ELAPSED_RANGE_TOOLTIP = (
      <>
        Enter the day range (based on the number of days between the transaction
        date and today) that qualifies this approval flow for use.
        <br />
        (Leave blank if no restrictions apply)
      </>
    );

    const BUSINESS_UNIT_TOOLTIP = (
      <>
        If the transaction’s primary Business unit matches this business unit,
        this approval flow will be eligible for use.
        <br />
        (Leave blank if no restrictions apply)
      </>
    );

    const COMPANY_TOOLTIP = (
      <>
        If the company matches the company of the requested transaction, this
        approval flow will be eligible for use.
        <br />
        (Leave blank if no restrictions apply)
      </>
    );

    const USER_TOOLTIP = (
      <>
        Select a user who, when acting as the requester, would qualify this
        approval flow for use.
        <br />
        (Leave blank if no restrictions apply)
      </>
    );

    const ROW_SECURITY = (
      <>
        If the transaction being requested has been assigned this row security,
        this approval flow will be eligible for use.
        <br />
        (Leave blank if no restrictions apply)
      </>
    );

    const PO_TYPE_TOOLTIP = (
      <>
        If the requested transaction is a purchase order and matches this PO type,
        the approval flow will be eligible for use.
        <br />
        (Leave blank if no restrictions apply)
      </>
    );

    const TAG_TOOLTIP = (
      <>
        If the requested transaction has been assigned a tag that matches this
        one, the approval flow will be eligible for use.
        <br />
        (Leave blank if no restrictions apply)
      </>
    );

    const RANK_TOOLTIP = (
      <>
        If the user requesting approval is of the rank type selected, the approval
        flow will be eligible for use.
        <br />
        (Leave blank if no restrictions apply)
      </>
    );

    const AUTO_APPROVED_TOOLTIP =
      'If the approver has been granted auto-approval rights and this option is checked, the approval flow will be eligible for auto-approval.';
    const AND_CONDITION_TOOLTIP = 'Select this option to link this condition with the one positioned directly above, creating an AND condition.';
    const disabled = data ? !data?.isFlowUpdatable : false;
    
    return (
      <FormProvider {...formMethods}>
        {visibility && (
          <Modal
            isOpen
            dragOptions={dragOptions}
            isModeless={!isValid && !isNew}
            onDismiss={() => {
              if (isDirty && isValid) onHandleSave();
              else onClose();
            }}
            styles={{
              scrollableContent: {
                overflow: 'scroll',
                '::-webkit-scrollbar': {
                  width: 5,
                },
                '::-webkit-scrollbar-thumb': {
                  backgroundColor: theme.palette.neutralLight,
                  borderRadius: '6px',
                  width: 5,
                },
                '::-webkit-scrollbar-thumb:hover': {
                  backgroundColor: theme.palette.neutralLighter,
                },
              },
            }}
          >
            {/* Header */}
            <Stack
              horizontal
              grow={1}
              style={{
                position: 'fixed',
                backgroundColor: theme.palette.neutralLighterAlt,
                width: '100%',
                zIndex: 10,
              }}
              horizontalAlign="space-between"
              tokens={{ padding: '20px' }}
            >
              <Stack
                horizontal
                verticalAlign="center"
                tokens={{ childrenGap: 7 }}
              >
                <Text variant={'xLarge'}>{`${isNew ? 'Add Condition' : 'Edit Condition'
                  }`}</Text>
              </Stack>
              <CloseButton
                onClick={() => {
                  if (isDirty && isValid) onHandleSave();
                  else onClose();
                }}
                disabled={!isValid && !isNew}
              />
            </Stack>
            <Stack
              style={{
                width: 500,
                maxHeight: 600,
                backgroundColor: `${theme.palette.neutralLighterAlt}66`,
              }}
            >
              {/* Main Form */}
              <Stack tokens={{ childrenGap: 50, padding: '100px 20px' }}>
                {/* FORM HOOK TEXT DESCRIPTION */}
                <FormHookTextField
                  label="Description"
                  multiline
                  name={`description`}
                  disabled={disabled}
                />

                <Stack tokens={{ childrenGap: 10 }}>
                  <TooltipHost content={AMOUNT_RANGE_TOOLTIP} id="range">
                    <Stack horizontal tokens={{ childrenGap: 20 }}>
                      <Stack.Item className={commonStyles.flexHalf}>
                        <FormHookAmount
                          disabled={disabled}
                          name={`startAmountTotal`}
                          placeholder="From"
                          label="Amount Range:"
                          fixedDecimalScale
                          decimalScale={2}
                          allowNegative={false}
                        />
                      </Stack.Item>
                      <Stack.Item
                        className={commonStyles.flexHalf}
                        style={{ paddingTop: 28 }}
                      >
                        <FormHookAmount
                          name={`endAmountTotal`}
                          placeholder="To"
                          fixedDecimalScale
                          decimalScale={2}
                          allowNegative={false}
                          disabled={disabled}
                        />
                      </Stack.Item>
                    </Stack>
                  </TooltipHost>
                  <Stack horizontal tokens={{ childrenGap: 20 }}>
                    <Stack.Item className={commonStyles.flexHalf}>
                      <FormHookDropdown
                        options={currencies}
                        label="Currency"
                        name={`requesterCurrencyId`}
                        disabled={disabled}
                        toolTipText={CURRENCY_TOOLTIP}
                      />
                    </Stack.Item>
                    <Stack.Item className={commonStyles.flexHalf}>
                      <FormHookDropdown
                        label="Conversions option"
                        options={conversionOptions}
                        width={240}
                        name={`amountConversionTypeId`}
                        disabled={disabled}
                        toolTipText={CONVERSION_TOOLTIP}
                      />
                    </Stack.Item>
                  </Stack>
                </Stack>

                <Stack tokens={{ childrenGap: 10 }}>
                  <Stack horizontal tokens={{ childrenGap: 20 }}>
                    <Stack.Item className={commonStyles.flexHalf}>
                      <FormHookDropdown
                        options={occupations}
                        label="Occupation"
                        name={`requesterUserOccupationTitleId`}
                        disabled={disabled}
                        toolTipText={OCCUPATION_TOOLTIP}
                      />
                    </Stack.Item>
                    <Stack.Item className={commonStyles.flexHalf}>
                      <FormHookDropdown
                        name={`requesterDepartmentId`}
                        label="Department"
                        options={departments}
                        disabled={disabled}
                        toolTipText={DEPARTMENT_TOOLTIP}
                      />
                    </Stack.Item>
                  </Stack>

                  <Stack horizontal tokens={{ childrenGap: 20 }}>
                    <Stack.Item className={commonStyles.flexHalf}>
                      <FormHookDropdown
                        options={departmentTypes}
                        label="Department types"
                        name={`requesterDepartmentTypeId`}
                        disabled={disabled}
                        toolTipText={DEPARTMENT_TYPE_TOOLTIP}
                      />
                    </Stack.Item>
                    <Stack.Item className={commonStyles.flexHalf}>
                      <FormHookDropdown
                        name={`requesterDepartmentGroupId`}
                        label="Department Group"
                        options={departmentGroups}
                        disabled={disabled}
                        toolTipText={DEPARTMENT_GROUP_TOOLTIP}
                      />
                    </Stack.Item>
                  </Stack>
                </Stack>
                <TooltipHost content={ELAPSED_RANGE_TOOLTIP} id="elapsedRange">
                  <Stack horizontal tokens={{ childrenGap: 20 }}>
                    <Stack.Item className={commonStyles.flexHalf}>
                      <FormHookAmount
                        // allowZeroValue
                        inputMode="decimal"
                        label="Elapsed Day Range"
                        name={`startElapsedDays`}
                        maxLength={4}
                        disabled={disabled}
                        placeholder="From"
                      />
                    </Stack.Item>
                    <Stack.Item
                      className={commonStyles.flexHalf}
                      style={{ paddingTop: 28 }}
                    >
                      <FormHookAmount
                        // allowZeroValue
                        placeholder="To"
                        inputMode="decimal"
                        name={`endElapsedDays`}
                        maxLength={4}
                        disabled={disabled}
                      />
                    </Stack.Item>
                  </Stack>
                </TooltipHost>
                <Stack tokens={{ childrenGap: 10 }}>
                  <Stack.Item className={commonStyles.flexHalf}>
                    <FormHookDropdown
                      name={`requesterBusinessUnitId`}
                      label="Business Unit"
                      options={additionalBusUnit}
                      disabled={disabled}
                      toolTipText={BUSINESS_UNIT_TOOLTIP}
                    />
                  </Stack.Item>
                  <Stack horizontal tokens={{ childrenGap: 20 }}>
                    <Stack.Item className={commonStyles.flexHalf}>
                      <FormHookDropdown
                        options={companies}
                        name={`requesterCompanyId`}
                        label="Company"
                        disabled={disabled}
                        toolTipText={COMPANY_TOOLTIP}
                      />
                    </Stack.Item>

                    <Stack.Item className={commonStyles.flexHalf}>
                      <FormHookDropdown
                        name={`requesterUserId`}
                        label="User"
                        options={users}
                        disabled={disabled}
                        toolTipText={USER_TOOLTIP}
                      />
                    </Stack.Item>
                  </Stack>
                  <Stack horizontal tokens={{ childrenGap: 20 }}>
                    <Stack.Item className={commonStyles.flexHalf}>
                      <FormHookDropdown
                        options={rowSecurity}
                        label="Row Security"
                        name={`requesterSecureRowLevelId`}
                        disabled={disabled}
                        toolTipText={ROW_SECURITY}
                      />
                    </Stack.Item>
                    <Stack.Item className={commonStyles.flexHalf}>
                      <FormHookDropdown
                        name={`requesterPurchaseOrderTypeId`}
                        label="PO Type"
                        options={purchaseOrder}
                        disabled={disabled}
                        toolTipText={PO_TYPE_TOOLTIP}
                      />
                    </Stack.Item>
                  </Stack>

                  <FormHookDropdown
                    options={tags}
                    label="Tag"
                    name={`requesterTagId`}
                    disabled={disabled}
                    toolTipText={TAG_TOOLTIP}
                  />
                  <Stack style={{ marginTop: 30 }}>
                    <FormHookDropdown
                      name={`requesterUserRankId`}
                      onRenderLabel={() => (
                        <OnRenderRankLabel
                          onIconClick={() => setOpenRankModal(true)}
                          disabled={disabled}
                        />
                      )}
                      options={ranks}
                      disabled={disabled}
                      toolTipText={RANK_TOOLTIP}
                      onRenderOption={(option) =>
                        option ? (
                          <OptionWithIcons
                            option={option as rankOptionType}
                            setRankDeleteEntity={setRankDeleteEntity}
                            setRankDeleteDialog={setRankDeleteDialog}
                            setOpenRankModal={setOpenRankModal}
                            setSelectedOption={setSelectedOption}
                            currentuserRankId={currentuserRankId}
                          />
                        ) : null
                      }
                    />
                  </Stack>
                </Stack>
                {openRankModal && (
                  <RankOptionViewModal
                    onClose={() => {
                      setSelectedOption(undefined);
                      setOpenRankModal(false);
                    }}
                    option={selectedOption}
                    onCreate={(id) => {
                      setValue('requesterUserRankId', id);
                      handleUserRankUpdate?.(id);
                    }}
                  />
                )}

                {rankDeleteEntity && rankDeleteDialog && (
                  <RankOptionDelete
                    rankDeleteEntity={rankDeleteEntity}
                    setRankDeleteDialog={setRankDeleteDialog}
                    onClose={() => {
                      setRankDeleteEntity(undefined);
                      setOpenRankModal(false);
                    }}
                  />
                )}

                <Stack horizontal tokens={{ childrenGap: 40 }}>
                  <TooltipHost content={AUTO_APPROVED_TOOLTIP} id="autoApprove">
                    <FormHookCheckBox
                      label="Auto approved"
                      boxSide="end"
                      name={`isAutoApprovedEligible`}
                      disabled={disabled}
                    />
                  </TooltipHost>
                  <TooltipHost content={AND_CONDITION_TOOLTIP} id="andCondition">
                    <FormHookCheckBox
                      label="And condition"
                      boxSide="end"
                      name={`isAndCondition`}
                      disabled={disabled}
                    />
                  </TooltipHost>
                </Stack>
              </Stack>

              {/* Footer */}
              <Stack
                horizontal
                style={{
                  position: 'fixed',
                  backgroundColor: `${theme.palette.neutralLighterAlt}`,
                  width: '100%',
                  zIndex: 10,
                  bottom: 0,
                }}
                tokens={{ padding: '10px 20px 20px 20px', childrenGap: 10 }}
              >
                <DefaultButton
                  text="Undo Changes"
                  onClick={onClose}
                  disabled={!isDirty && !isNew}
                />
                <Stack style={{ marginLeft: 'auto' }}>
                  <DefaultButton
                    text="Close"
                    onClick={() => {
                      if (isDirty && isValid) {
                        onHandleSave();
                      } else onClose();
                    }}
                    disabled={!isValid && !isNew}
                  />
                </Stack>
              </Stack>
            </Stack>
          </Modal>
        )}
      </FormProvider>
    );
  };
